import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import {
  DEFAULT_QUANTIFY_TOP_SELECTION_VALUE,
  TOP_QUANTIFY_SELECTION_OPTIONS,
} from '@modules/quantify/constants';
import { useFetchQuantifyReport } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, MenuList } from '@mui/material';
import { useEffect, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { shallow } from 'zustand/shallow';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

export const QuantifyTopSelectionMenu = () => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const fetchQuantifyReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const dispatch = useQuantifyStore((state) => state.dispatch);
  const topDataPointsSelectionCount = useQuantifyStore(
    ({ query }) =>
      query.topDataPointsSelectionCount ?? DEFAULT_QUANTIFY_TOP_SELECTION_VALUE,
  );
  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupByField?.displayName,
  );
  const totalAggregateHits = useQuantifyStore(
    ({ visual }) => visual.totalAggregateHits,
  );
  const isSearchTermPresent = useQuantifyStore(
    ({ visual }) => !!visual.searchTerm,
  );

  const { title: name, href } = useAnalysisStore(
    ({ title, href, scope }) => ({ title, href, scope }),
    shallow,
  );

  useEffect(() => {
    if (shouldFetchReportData) {
      fetchQuantifyReportData();
      setShouldFetchReportData(false);
    }
  }, [shouldFetchReportData, fetchQuantifyReportData]);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const onSelectOption = (selected: number) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        topDataPointsSelectionCount: selected,
      },
    });
    setShouldFetchReportData(true);
    closeMenu();
    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Selected Top X Option',
      name,
      link: href,
      query: {
        showMeLabel: groupByLabel,
      },
      topXOption: selected,
    });
  };

  const isMenuOpen = Boolean(menuAnchorEl);

  return (
    <Box>
      <Button
        size="small"
        variant="outlined"
        endIcon={<KeyboardArrowDownRounded />}
        onClick={openMenu}
        sx={{
          whiteSpace: 'nowrap',
          minWidth: 'max-content',
        }}
        disabled={
          isSearchTermPresent ||
          totalAggregateHits < TOP_QUANTIFY_SELECTION_OPTIONS[0]
        }
      >
        Top{' '}
        {totalAggregateHits < topDataPointsSelectionCount
          ? totalAggregateHits
          : topDataPointsSelectionCount}
      </Button>
      <Menu
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuList>
          {TOP_QUANTIFY_SELECTION_OPTIONS.map((option) => {
            return (
              <MenuItem
                key={option}
                onClick={() => onSelectOption(option)}
                selected={option === topDataPointsSelectionCount}
                disabled={totalAggregateHits < option}
                data-testid={`top-${option}`}
              >
                Top {option}
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Box>
  );
};
